import type { PaginatedResponse } from '@/api/pagination'
import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import { STALE_TIME_LONG } from '@/lib/constants'
import { queryKeys } from '@/lib/query-keys'

export type OrganisationPermission =
  | 'login'
  | 'manage_users'
  | 'manage_organisation'
  | 'view_locations'
  | 'manage_locations'
  | 'view_machines'
  | 'manage_machines'
  | 'view_orders'
  | 'manage_stock'
  | 'manage_prices'
  | 'view_invoices'
  | 'export_sales'
  | 'place_orders'
  | 'manage_wallets'

export interface Organisation {
  id: number
  name: string
  permissions: OrganisationPermission[]
  locations_count: number
  machines_count: number
  machines_with_warnings_count: number
  products_count: number
  users_count: number
  system_managers_count: number
  operators_count: number
  company_wallet_enabled: boolean
}

export interface OrganisationInvoiceAddress {
  name: string | null
  address: string | null
  zip_code: string | null
  city: string | null
  country: string | null
}

export type OrganisationInvoiceAddressInput = {
  [K in keyof OrganisationInvoiceAddress]: string
}

export interface OrganisationDetail extends Organisation {
  address: string | null
  city: string | null
  zip_code: string | null
  country: string | null
  coc_number: string | null
  debtor_number: string | null
  invoice_email: string | null
  vat_code: string | null
  iban: string | null
  account_holder: string | null
  uses_sepa: boolean
  invoice_reference: string | null
  different_invoice_address: boolean
  invoice_address: OrganisationInvoiceAddress
}

export async function fetchOrganisations(): Promise<Organisation[]> {
  const response = await api.get<PaginatedResponse<Organisation>>('/organisations')
  return response.data
}

export async function fetchOrganisation(organisationId: number): Promise<OrganisationDetail> {
  return api.get<OrganisationDetail>(`/organisations/${organisationId}`)
}

export async function updateOrganisation(
  organisationId: number,
  data: Partial<
    Pick<
      OrganisationDetail,
      | 'name'
      | 'address'
      | 'city'
      | 'zip_code'
      | 'country'
      | 'coc_number'
      | 'invoice_email'
      | 'vat_code'
      | 'iban'
      | 'account_holder'
      | 'invoice_reference'
      | 'different_invoice_address'
    >
  > & {
    invoice_address?: OrganisationInvoiceAddressInput
  },
): Promise<{ message: string; data: OrganisationDetail }> {
  return api.patch<{ message: string; data: OrganisationDetail }>(
    `/organisations/${organisationId}`,
    { body: data },
  )
}

export function organisationsQueryOptions() {
  return queryOptions({
    queryKey: queryKeys.organisations.all,
    queryFn: async () => fetchOrganisations(),
    staleTime: STALE_TIME_LONG,
  })
}

export function organisationQueryOptions(organisationId: number) {
  return queryOptions({
    queryKey: queryKeys.organisations.detail(organisationId),
    queryFn: async () => fetchOrganisation(organisationId),
    staleTime: STALE_TIME_LONG,
  })
}
